import {
  Box,
  Fade,
  Link,
  Paper,
  Popper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useCallback } from "react";
import { Typography } from "@platform/design-system";

import { downloadDocumentFile, openBinaryPdfInWindow } from "./documentViewer";

import type { AxiosInstance } from "axios";

const HEADER_LABELS = [
  "Class Name",
  "Field Name",
  "Value",
  "Page No.",
  "Justification",
];

const colorTextPrimary = "text.primary";
const PAGE_LINK_SX = {
  fontSize: 15,
  fontWeight: "fontWeightRegular",
  color: "brandSupporting.main",
  cursor: "pointer",
} as const;
const VIEW_ERROR_MESSAGE =
  "Unable to view the document right now. Please try again.";

/** Who produced a field's value and evidence: document extraction or RUMI. */
export type FieldSource = "extraction" | "rumi";

const CARD_TITLES: Readonly<Record<FieldSource, string>> = {
  extraction: "AI Extracted Field",
  rumi: "RUMI",
};

export interface AIExtractedRow {
  className: string;
  fieldName: string;
  value: string;
  pdfPage: string;
  pdfPageNumber: number;
  justification: string;
  documentDownloadUrl?: string;
  documentName?: string | null;
  /** Omitted by callers that only show extraction output. */
  source?: FieldSource;
}

export interface AIExtractedFieldCardProps {
  /** Column header labels. Defaults to Class Name, Field Name, Value, Page No., Justification */
  headerLabels?: string[];
  row: AIExtractedRow;
  onPageClick?: (pageNumber: number, row: AIExtractedRow) => void;
  anchorEl?: HTMLElement | null;
  open?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Axios instance for downloading documents. Required for page links to work. */
  axiosInstance?: AxiosInstance;
  /** Error callback. Called when document download fails. */
  onError?: (message: string) => void;
  /** Width of the popover. Defaults to "75vw". */
  width?: string;
  /** Custom table body content. When provided, replaces the default row rendering. */
  children?: React.ReactNode;
}

const TableHeaderCell: React.FC<{ label: string }> = ({ label }) => (
  <TableCell
    sx={{
      fontSize: 15,
      pt: 1,
      borderColor: "grey.200",
      color: colorTextPrimary,
      fontWeight: "fontWeightMedium",
    }}
  >
    {label}
  </TableCell>
);

const TableValueCell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <TableCell sx={{ pt: 2, borderBottom: "none" }}>
    <Typography variant="body-medium">{children}</Typography>
  </TableCell>
);

export const AIExtractedFieldCard: React.FC<AIExtractedFieldCardProps> = ({
  headerLabels = HEADER_LABELS,
  row,
  onPageClick,
  anchorEl,
  open = false,
  onMouseEnter,
  onMouseLeave,
  axiosInstance,
  onError,
  width = "75vw",
  children,
}): React.JSX.Element => {
  const handleViewDocument = useCallback((): void => {
    const downloadLink = row.documentDownloadUrl;

    if (!downloadLink) {
      if (onPageClick) {
        onPageClick(row.pdfPageNumber, row);
      }
      return;
    }

    if (!axiosInstance) {
      onError?.(VIEW_ERROR_MESSAGE);
      return;
    }

    const isPdf = downloadLink.toLowerCase().endsWith(".pdf");

    if (!isPdf) {
      onError?.(
        "Only PDF documents can be previewed. Please download the file to view it.",
      );
      return;
    }

    const viewerWindow = window.open("", "_blank");

    if (!viewerWindow) {
      onError?.(VIEW_ERROR_MESSAGE);
      return;
    }

    // Keep a usable window reference while preventing opener access.
    viewerWindow.opener = null;

    void (async () => {
      try {
        if (
          !openBinaryPdfInWindow(
            viewerWindow,
            await downloadDocumentFile(downloadLink, axiosInstance),
            row.pdfPageNumber,
          )
        ) {
          viewerWindow.close();
          onError?.(VIEW_ERROR_MESSAGE);
        }
      } catch {
        viewerWindow.close();
        onError?.(VIEW_ERROR_MESSAGE);
      }
    })();
  }, [axiosInstance, onPageClick, row, onError]);

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top"
      transition
      style={{ zIndex: 1300 }}
      modifiers={[{ name: "preventOverflow", options: { padding: 64 } }]}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={150}>
          <Paper
            elevation={4}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            sx={{
              width,
              mt: 1,
              p: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                pb: 1,
                px: 1,
              }}
            >
              <Box>
                <Typography>
                  <strong>{CARD_TITLES[row.source ?? "extraction"]}</strong>
                </Typography>
                {row.documentName && (
                  <Typography variant="body-medium">
                    {row.documentName}
                  </Typography>
                )}
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {headerLabels.map((col) => (
                      <TableHeaderCell key={col} label={col} />
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {children ?? (
                    <TableRow>
                      <TableValueCell>{row.className || "-"}</TableValueCell>
                      <TableValueCell>{row.fieldName || "-"}</TableValueCell>
                      <TableValueCell>{row.value || "-"}</TableValueCell>

                      <TableCell sx={{ pt: 2, borderBottom: "none" }}>
                        {row.documentDownloadUrl ? (
                          <Link
                            component="button"
                            type="button"
                            underline="always"
                            onClick={handleViewDocument}
                            sx={PAGE_LINK_SX}
                          >
                            {row.pdfPage || "-"}
                          </Link>
                        ) : onPageClick ? (
                          <Link
                            component="button"
                            type="button"
                            underline="always"
                            onClick={() => onPageClick(row.pdfPageNumber, row)}
                            sx={PAGE_LINK_SX}
                          >
                            {row.pdfPage || "-"}
                          </Link>
                        ) : (
                          <Typography variant="body-medium">
                            {row.pdfPage || "-"}
                          </Typography>
                        )}
                      </TableCell>

                      <TableValueCell>
                        {row.justification || "-"}
                      </TableValueCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};
